// src/components/music/parts/MiniPlayerEnhanced.jsx

import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {
  Play,
  Pause,
  SkipBack,
  SkipForward,
  Shuffle,
  Repeat,
  Minimize2,
  Volume2,
  VolumeX,
  Settings
} from 'lucide-react';
import { formatTime } from '../utils/audioUtils';
import { getTypeLabel } from '../utils/badgeUtils';
import { REPEAT_MODES } from '../constants/repeatModes';

export const MiniPlayer = ({
  currentTrack,
  isPlaying,
  isVisible,
  currentTime,
  duration,
  isShuffling,
  repeatMode,
  currentIndex,
  playQueue,
  onPlayPause,
  onPrev,
  onNext,
  onShuffle,
  onRepeat,
  onProgressClick,
  onHide,
  onShow,
  currentUser,
  volume,
  onVolumeChange,
  isMuted,
  onMuteToggle,
  speed,
  onSpeedChange,
  onResetAudioControls,
  compression,
  onCompressionChange
}) => {
  const [showAudioControls, setShowAudioControls] = useState(false);

  if (!currentTrack) return null;

  const progressPercent = duration ? (currentTime / duration) * 100 : 0;
  const speedPresets = [0.5, 0.75, 1, 1.25, 1.5, 2];

  return (
    <>
      {isVisible ? (
        <motion.div
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 100, opacity: 0 }}
          className="fixed bottom-6 left-6 right-6 md:left-auto md:right-6 md:w-full md:max-w-md bg-[#121318]/90 backdrop-blur-xl text-white rounded-2xl shadow-2xl p-4 z-40 border border-white/10 transition-all duration-300"
        >
          <div className="flex flex-col gap-3">
            {/* Track Info */}
            <div className="flex justify-between items-start">
              <div className="flex-1 pr-2">
                <p className="font-semibold text-sm line-clamp-1 text-white">{currentTrack.name}</p>
                <p className="text-xs text-white/70">{currentTrack.artist || 'The Asidors'}</p>
                <div className="flex items-center gap-2 mt-1">
                  <span className="text-xs bg-[#bd9f67]/20 text-[#bd9f67] border border-[#bd9f67]/30 px-2 py-0.5 rounded">
                    {getTypeLabel(currentTrack.type || 'song')}
                  </span>
                  {!currentUser && (
                    <span className="text-xs bg-[#bd9f67]/20 text-[#bd9f67] px-2 py-0.5 rounded flex items-center gap-1 ml-auto">
                      ⏱️ 15s preview
                    </span>
                  )}
                </div>
              </div>
              <button
                onClick={() => setShowAudioControls(!showAudioControls)}
                className="p-1 rounded-full text-white/80 hover:bg-white/10 transition"
              >
                <Settings size={18} />
              </button>
              <button onClick={onHide} className="p-1 rounded-full text-white/80 hover:bg-white/10 transition">
                <Minimize2 size={18} />
              </button>
            </div>

            {/* Progress Bar */}
            <div className="flex flex-col gap-2">
              <div onClick={onProgressClick} className="w-full bg-white/10 rounded-full h-1.5 cursor-pointer hover:h-2 transition-all">
                <div className="bg-[#bd9f67] h-full rounded-full transition-all" style={{ width: `${progressPercent}%` }} />
              </div>
              <div className="flex items-center justify-between text-xs text-white/70 gap-2 font-mono">
                <span>{formatTime(currentTime)}</span>
                <span>{formatTime(duration)}</span>
              </div>
            </div>

            {/* Main Controls */}
            <div className="flex items-center justify-center gap-4">
              <button onClick={onShuffle} className={`p-2 rounded-full ${isShuffling ? 'text-[#bd9f67]' : 'text-white/40'}`}>
                <Shuffle size={18} />
              </button>
              <button onClick={onPrev} disabled={currentIndex === 0 && repeatMode !== REPEAT_MODES.ALL} className="p-2 disabled:opacity-50 text-white/80 hover:text-white">
                <SkipBack size={18} fill="currentColor" />
              </button>
              <button onClick={onPlayPause} className="bg-[#bd9f67] text-gray-950 p-3 rounded-full shadow-lg hover:bg-[#d0b07a] transition">
                {isPlaying ? <Pause size={24} fill="currentColor" /> : <Play size={24} fill="currentColor" className="ml-0.5" />}
              </button>
              <button onClick={onNext} disabled={currentIndex === playQueue.length - 1 && repeatMode !== REPEAT_MODES.ALL} className="p-2 disabled:opacity-50 text-white/80 hover:text-white">
                <SkipForward size={18} fill="currentColor" />
              </button>
              <button onClick={onRepeat} className={`p-2 rounded-full ${repeatMode === REPEAT_MODES.OFF ? 'text-white/40' : 'text-[#bd9f67]'}`}>
                <Repeat size={18} className={repeatMode === REPEAT_MODES.ONE ? 'border border-[#bd9f67] rounded-sm' : ''} />
              </button>
            </div>

            {/* Volume Control */}
            <div className="flex items-center gap-2 bg-black/30 rounded-lg p-2 border border-white/5">
              <button onClick={onMuteToggle} className="text-white/70 hover:text-[#bd9f67] transition">
                {isMuted ? <VolumeX size={18} /> : <Volume2 size={18} />}
              </button>
              <input
                type="range"
                min="0"
                max="1"
                step="0.01"
                value={isMuted ? 0 : volume}
                onChange={(e) => onVolumeChange(parseFloat(e.target.value))}
                className="flex-1 h-1.5 accent-[#bd9f67]"
              />
              <span className="text-xs text-white/70 w-8 text-right font-mono">{Math.round(volume * 100)}%</span>
            </div>

            {/* Simplified Audio Controls Panel */}
            {showAudioControls && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                className="space-y-3 border-t border-white/10 pt-3 mt-3"
              >
                <div>
                  <div className="flex items-center justify-between text-xs mb-2">
                    <span className="text-white/70">Playback Speed</span>
                    <span className="text-[#bd9f67] font-semibold font-mono">{speed.toFixed(2)}x</span>
                  </div>
                  <div className="grid grid-cols-6 gap-1">
                    {speedPresets.map(s => (
                      <button
                        key={s}
                        onClick={() => onSpeedChange(s)}
                        className={`px-1 py-1 text-xs rounded transition ${
                          speed === s ? 'bg-[#bd9f67] text-gray-950 font-semibold' : 'bg-white/10 text-white/80 hover:bg-white/20'
                        }`}
                      >
                        {s}x
                      </button>
                    ))}
                  </div>
                </div>
              </motion.div>
            )}
          </div>
        </motion.div>
      ) : (
        <button onClick={onShow} className="fixed bottom-5 right-20 bg-[#121318]/90 backdrop-blur-xl border border-white/20 text-[#bd9f67] p-3 rounded-full shadow-2xl z-40 hover:bg-[#bd9f67] hover:text-black transition">
          <Play size={20} fill="currentColor" />
        </button>
      )}
    </>
  );
};